import { DOCUMENT } from "@angular/common";
import {
  Component,
  effect,
  inject,
  Inject,
  untracked,
  NgZone,
  OnDestroy,
  OnInit,
  SecurityContext,
  signal,
} from "@angular/core";
import { DomSanitizer } from "@angular/platform-browser";
import { MatDialog } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { ApmService } from "@elastic/apm-rum-angular";

import { IndividualConfig, ToastrService } from "ngx-toastr";
import { distinctUntilChanged, map, Subject, takeUntil } from "rxjs";

import { ModalService } from "@bitwarden/angular/services/modal.service";
import { AuthService } from "@bitwarden/common/abstractions/auth.service";
import { BroadcasterService } from "@bitwarden/common/abstractions/broadcaster.service";
import { CipherService } from "@bitwarden/common/abstractions/cipher.service";
import { CollectionService } from "@bitwarden/common/abstractions/collection.service";
import { CryptoService } from "@bitwarden/common/abstractions/crypto.service";
import { EventUploadService } from "@bitwarden/common/abstractions/event/event-upload.service";
import { InternalFolderService } from "@bitwarden/common/abstractions/folder/folder.service.abstraction";
import { I18nService } from "@bitwarden/common/abstractions/i18n.service";
import { KeyConnectorService } from "@bitwarden/common/abstractions/keyConnector.service";
import { LogService } from "@bitwarden/common/abstractions/log.service";
import { ThemeType } from "@bitwarden/common/enums/themeType";
import { AbstractThemingService } from "@bitwarden/angular/services/theming/theming.service.abstraction";
import { NotificationsService } from "@bitwarden/common/abstractions/notifications.service";
import { PasswordGenerationService } from "@bitwarden/common/abstractions/passwordGeneration.service";
import { PlatformUtilsService } from "@bitwarden/common/abstractions/platformUtils.service";
import { InternalPolicyService } from "@bitwarden/common/abstractions/policy/policy.service.abstraction";
import { SearchService } from "@bitwarden/common/abstractions/search.service";
import { SettingsService } from "@bitwarden/common/abstractions/settings.service";
import { StateService } from "@bitwarden/common/abstractions/state.service";
import { SyncService } from "@bitwarden/common/abstractions/sync/sync.service.abstraction";
import { VaultTimeoutService } from "@bitwarden/common/abstractions/vaultTimeout/vaultTimeout.service";

import { ApmConfig } from "../../config/apm-config";
import { getAdManager } from "@bitwarden/web-vault/app/capacitor-plugins/ad-manager";
import { subscribeToAppUrlOpen } from "@bitwarden/web-vault/app/capacitor-plugins/app";
import { initializeStatusBar, setStatusBarLogService } from "./capacitor-plugins/status-bar";
import { RouterService } from "./core";
import { AppStateStoreService } from "@bitwarden/web-vault/app/services/store/app-state/app-state.store.service";
import { StoreLoaderService } from "@bitwarden/web-vault/app/services/dali/store.loader.service";
import { UserStoreService } from "@bitwarden/web-vault/app/services/store/user/user.store.service";
import { GlossSync } from "@bitwarden/web-vault/app/services/syncing/gloss.sync";
import { Origin } from "@bitwarden/web-vault/app/models/types/general-types";
import { PaymentService } from "@bitwarden/web-vault/app/services/subscription/payment.service";
import { AdStateService } from "@bitwarden/web-vault/app/services/credits/ad-state.service";
import { CreditsService } from "@bitwarden/web-vault/app/services/syncing/gloss-credits/credits.service";
import { ConfirmationDialogService } from "@bitwarden/web-vault/app/services/confirmation/confirmation.service";
import { NotificationEnum } from "@bitwarden/web-vault/app/models/enum/notification.enum";
import { GuideWizardService } from "@vault/web-vault/app/services/guideWizard/guide-wizard.service";
import { PerformanceService } from "@bitwarden/web-vault/app/services/performance/performance.service";
import { Bootstrap } from "./services/app.bootstrap.service";
import { VaultSyncCacheService } from "@bitwarden/web-vault/app/services/syncing/vault-sync-cache.service";
import { DialogFactory } from "@bitwarden/web-vault/app/components/dialog/dialogFactory";
import { UserThemeService } from "@bitwarden/web-vault/app/services/theming/user-theme.service";
import { AppUpdateService } from "@bitwarden/web-vault/app/services/app-update/app-update.service";
import { GlossSyncService } from "@bitwarden/web-vault/app/services/syncing/gloss.sync.service";
import { RoleAccessService } from "@bitwarden/web-vault/app/services/permission/role-access.service";
import { DeviceApprovalWatcherService } from "@vault/web-vault/app/services/DataService/device-trust/device-approval-watcher.service";
import { GlossScrollDialogsService } from "@bitwarden/web-vault/app/components/gloss-scroll/gloss-scroll-dialogs.service";
import { DialogEscapeService } from "@bitwarden/web-vault/app/services/dialog-escape.service";
import { NAV_LAYOUT_CACHE_KEY } from "@bitwarden/web-vault/app/layouts/menu/app-menu/nav-layout-cache";
const BroadcasterSubscriptionId = "AppComponent";
const IdleTimeout = 60000 * 10; // 10 minutes

@Component({
  selector: "app-root",
  templateUrl: "app.component.html",
})
export class AppComponent implements OnDestroy, OnInit {
  private lastActivity: number = null;
  private idleTimer: number = null;
  private isIdle = false;

  private destroy$ = new Subject<void>();
  private dialogFactory: DialogFactory;
  private userStoreService = inject(UserStoreService);

  /** This is the new app state using signal **/
  private appState: AppStateStoreService = inject(AppStateStoreService);

  /** Sync accounts after login */
  private glossSync = inject(GlossSync);
  private paymentService: PaymentService = inject(PaymentService);

  private adStateService = inject(AdStateService);
  private creditsService = inject(CreditsService);
  private notifyService = inject(ConfirmationDialogService);
  private guideWizardService = inject(GuideWizardService);

  private bootstrap = inject(Bootstrap);
  private userThemeService = inject(UserThemeService);
  private appUpdateService = inject(AppUpdateService);
  private vaultSyncCacheService = inject(VaultSyncCacheService);
  private glossSyncService = inject(GlossSyncService);
  private roleAccessService = inject(RoleAccessService);
  private dialog = inject(MatDialog);
  private modalService = inject(ModalService);
  private deviceApprovalWatcher = inject(DeviceApprovalWatcherService);
  private themingService = inject(AbstractThemingService);
  // Puts GlossScroll's overlay bar on every dialog as it opens.
  private glossScrollDialogs = inject(GlossScrollDialogsService);
  // Lets Escape close disableClose dialogs (keyboard users otherwise only have the X button).
  private dialogEscape = inject(DialogEscapeService);

  private appKnowsVaultIsReady = signal(false);

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private broadcasterService: BroadcasterService,
    private folderService: InternalFolderService,
    private settingsService: SettingsService,
    private syncService: SyncService,
    private passwordGenerationService: PasswordGenerationService,
    private cipherService: CipherService,
    private authService: AuthService,
    private router: Router,
    private toastrService: ToastrService,
    private i18nService: I18nService,
    private platformUtilsService: PlatformUtilsService,
    private ngZone: NgZone,
    private vaultTimeoutService: VaultTimeoutService,
    private cryptoService: CryptoService,
    private collectionService: CollectionService,
    private sanitizer: DomSanitizer,
    private searchService: SearchService,
    private notificationsService: NotificationsService,
    private routerService: RouterService,
    private stateService: StateService,
    private eventUploadService: EventUploadService,
    private policyService: InternalPolicyService,
    private keyConnectorService: KeyConnectorService,
    private performanceService: PerformanceService,
    private apmService: ApmService,
    private storeLoaderService: StoreLoaderService,
    private logService: LogService,
  ) {
    this.dialogFactory = new DialogFactory();
    this.loadApmInstance();
    setStatusBarLogService(this.logService); // Set LogService once for the module
    const adManager = getAdManager(this.logService);
    adManager.initialize();
    // Keep status bar icons/background in sync with the effective theme (incl. System following the OS)
    this.themingService.theme$
      .pipe(
        map((t) => t.effectiveTheme === ThemeType.Dark),
        distinctUntilChanged(),
        takeUntil(this.destroy$),
      )
      .subscribe((isDark) => void initializeStatusBar(isDark));
    adManager.subscribeToFailedToLoad((error, provider) => {
      // Only show toast if we're not falling back to another provider
      const message = adManager.getErrorMessage(error, provider);
      this.platformUtilsService.showToast(
        "error",
        "adFailedToLoad",
        `${provider} error code: ${error.code} ${message}`,
      );
    });

    // Vault is ready, initialize stores
    effect(() => {
      const appKnowsVaultIsReady = this.appKnowsVaultIsReady();
      if (!appKnowsVaultIsReady) {
        return;
      }

      untracked(() => {
        void this.bootstrap.initializeStores();
      });
    });

    // Stores are ready; bootstrap waits inside for user location when needed
    effect(() => {
      if (!this.bootstrap.isStoreReady()) {
        return;
      }

      untracked(() => {
        void this.bootstrap.bootstrap();
      });
    });
  }

  ngOnInit() {
    this.i18nService.locale$.pipe(takeUntil(this.destroy$)).subscribe((locale) => {
      this.document.documentElement.lang = locale;
    });

    this.ngZone.runOutsideAngular(() => {
      window.onmousemove = () => this.recordActivity();
      window.onmousedown = () => this.recordActivity();
      window.ontouchstart = () => this.recordActivity();
      window.onclick = () => this.recordActivity();
      window.onscroll = () => this.recordActivity();
      window.onkeypress = () => this.recordActivity();
    });

    if (this.platformUtilsService.isMobileDevice()) {
      subscribeToAppUrlOpen((event) => {
        this.ngZone.run(() => {
          const [_, path] = event.url.split("#");
          if (path) {
            this.router.navigateByUrl(path);
          }
        });
      });
    }

    this.broadcasterService.subscribe(BroadcasterSubscriptionId, async (message: any) => {
      this.ngZone.run(async () => {
        switch (message.command) {
          case "loggedIn": {
            this.notificationsService.updateConnection(false);
            this.appState.isNewLogin.set(true);
            this.performanceService.recordSuccessfulLogin();
            this.appUpdateService
              .checkForUpdate()
              .catch((e) => this.logService.error(e instanceof Error ? e.message : String(e)));
            const loginUserId = await this.stateService.getUserId();
            const cached = this.vaultSyncCacheService.restoreCache(loginUserId);
            // eslint-disable-next-line no-console
            console.log(
              "[VaultCache] cache hit:",
              !!cached,
              cached ? `lastSync=${cached.lastSync.toISOString()}` : "(full sync)",
            );
            if (cached) {
              await this.syncService.setLastSync(cached.lastSync);
              await this.cipherService.replace(cached.ciphers);
            }
            break;
          }
          case "loggedOut":
            // Logout only navigates, which doesn't close Material dialogs; without this an open
            // dialog stays over the login page showing the previous user's data.
            this.dialog.closeAll();
            // Legacy ModalService prompts (import/export password) are attached to <body>, so
            // navigation doesn't remove them either.
            this.modalService.closeAll();
            this.storeLoaderService.clearStores();
            this.appState.clearStore();
            this.glossSyncService.resetBootstrapSession();
            this.routerService.setPreviousUrl("/");
            this.appKnowsVaultIsReady.set(false);
            this.notificationsService.updateConnection(false);
            this.deviceApprovalWatcher.reset();
            // Resets the in-memory role data and also drops the persisted (global-state)
            // roleScope/roleClaim so the next login can't inherit them (#4981); storage goes first.
            await this.roleAccessService
              .clearForLogout()
              .catch((e) => this.logService.error(e instanceof Error ? e.message : String(e)));
            break;
          case "unlocked":
            this.notificationsService.updateConnection(false);
            void this.deviceApprovalWatcher.check();
            break;
          case "authBlocked":
            this.routerService.setPreviousUrl(message.url);
            this.router.navigate(["/"]);
            break;
          case "logout":
            this.logOut(!!message.expired);
            break;
          case "lockVault":
            await this.vaultTimeoutService.lock();
            break;
          case "locked":
            // Same as loggedOut: an open dialog would otherwise stay over the lock screen.
            this.dialog.closeAll();
            this.modalService.closeAll();
            this.notificationsService.updateConnection(false);
            this.deviceApprovalWatcher.reset();
            this.router.navigate(["lock"]);
            break;
          case "lockedUrl":
            this.routerService.setPreviousUrl(message.url);
            break;
          case "syncStarted":
            this.appState.startProcess("vault-loading", "Retrieving data");

            break;
          case "syncCompleted": {
            this.appState.endProcess("vault-loading");
            if (message.isVaultReady && !this.appKnowsVaultIsReady()) {
              this.appKnowsVaultIsReady.set(true);
              // Covers TDE logins/refreshes, which unlock without an "unlocked" message.
              void this.deviceApprovalWatcher.check();
            }
            // Track whether the vault we're showing was confirmed against the server.
            // `verified === false` means we're serving cached data we couldn't verify
            // (offline) — plumbing for a future offline-mode indicator/retry.
            this.appState.setVaultVerified(message.verified !== false);
            if (message.successfully) {
              const syncUserId = await this.stateService.getUserId();
              const lastSync = await this.syncService.getLastSync();
              const ciphers = await this.stateService.getEncryptedCiphers();
              if (syncUserId && lastSync && ciphers) {
                this.vaultSyncCacheService.saveCache(syncUserId, lastSync, ciphers);
              }
            }
            break;
          }
          case "showToast":
            this.showToast(message);
            break;
          case "convertAccountToKeyConnector":
            this.router.navigate(["/remove-password"]);
            break;
          default:
            break;
        }
      });
    });
  }

  ngOnDestroy() {
    this.broadcasterService.unsubscribe(BroadcasterSubscriptionId);
    this.destroy$.next();
    this.destroy$.complete();
  }

  private async logOut(expired: boolean) {
    await this.eventUploadService.uploadEvents();
    const userId = await this.stateService.getUserId();
    const lastSync = await this.syncService.getLastSync();
    const ciphers = await this.stateService.getEncryptedCiphers();
    if (userId && lastSync && ciphers) {
      this.vaultSyncCacheService.saveCache(userId, lastSync, ciphers);
    }
    await Promise.all([
      this.syncService.setLastSync(new Date(0)),
      this.cryptoService.clearKeys(),
      this.settingsService.clear(userId),
      this.cipherService.clear(userId),
      this.folderService.clear(userId),
      this.collectionService.clear(userId),
      this.policyService.clear(userId),
      this.passwordGenerationService.clear(),
      this.keyConnectorService.clear(),
    ]);

    this.searchService.clearIndex();
    this.authService.logOut(async () => {
      if (expired) {
        this.platformUtilsService.showToast(
          "warning",
          this.i18nService.t("loggedOut"),
          this.i18nService.t("loginExpired"),
        );
      }

      if (userId) {
        localStorage.removeItem(`avatar_${userId}`);
        localStorage.removeItem(NAV_LAYOUT_CACHE_KEY + userId);
      }
      await this.stateService.clean({ userId: userId });
      this.router.navigate(["/"]);
    });
  }

  private async recordActivity() {
    const now = new Date().getTime();
    if (this.lastActivity != null && now - this.lastActivity < 250) {
      return;
    }

    this.lastActivity = now;
    this.stateService.setLastActive(now);
    // Idle states
    if (this.isIdle) {
      this.isIdle = false;
      this.idleStateChanged();
    }
    if (this.idleTimer != null) {
      window.clearTimeout(this.idleTimer);
      this.idleTimer = null;
    }
    this.idleTimer = window.setTimeout(() => {
      if (!this.isIdle) {
        this.isIdle = true;
        this.idleStateChanged();
        this.userStoreService.setShowBalance(false);
      }
    }, IdleTimeout);
  }

  private showToast(msg: any) {
    let message = "";

    const options: Partial<IndividualConfig> = {};

    if (typeof msg.text === "string") {
      message = msg.text;
    } else if (Array.isArray(msg.text)) {
      if (msg.text.length === 1) {
        message = msg.text[0];
      } else {
        msg.text.forEach(
          (t: string) =>
            (message += "<p>" + this.sanitizer.sanitize(SecurityContext.HTML, t) + "</p>"),
        );
        options.enableHtml = true;
      }
    } else {
      message = String(msg.text ?? "");
    }
    if (msg.options != null) {
      if (msg.options.trustedHtml === true) {
        options.enableHtml = true;
      }
      if (msg.options.timeout != null && msg.options.timeout > 0) {
        options.timeOut = msg.options.timeout;
      }
      if (msg.options.extraClass) {
        options.toastClass = `${options.toastClass ?? "ngx-toastr"} ${msg.options.extraClass}`;
      }
      if (msg.options.toastComponent) {
        options.toastComponent = msg.options.toastComponent;
      }
      if (msg.options.payload) {
        options.payload = msg.options.payload;
      }
    }

    if (options.timeOut == null) {
      const defaultTimeouts: Record<string, number> = { warning: 6000, error: 8000 };
      options.timeOut = defaultTimeouts[msg.type] ?? 4000;
    }

    this.toastrService.show(message, msg.title, options, "toast-" + msg.type);
  }

  private idleStateChanged() {
    if (this.isIdle) {
      this.notificationsService.disconnectFromInactivity();
    } else {
      this.notificationsService.reconnectFromActivity();
    }
  }

  private loadApmInstance() {
    const apmConfig = new ApmConfig();

    this.apmService.init({
      serviceName: apmConfig.SERVICE_NAME,
      serverUrl: apmConfig.SERVER_URL,
      environment: apmConfig.environment,
      serviceVersion: apmConfig.serviceVersion,
    });
  }
}
